import { formatDistanceToNowStrict, formatDistanceToNow, differenceInCalendarDays } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Helpers puros de formato de fechas. Aislados de los componentes para
 * testeo unit y para reutilización (DRY).
 */

const RE_SOLO_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

export function toDate(value: Date | string): Date {
  if (typeof value !== "string") return value;
  // Una fecha "solo día" (YYYY-MM-DD, sin hora) la interpreta `new Date()` como
  // medianoche UTC; al formatearla en zona local (p. ej. UTC-5) se corre al día
  // anterior (la "vista previa de resolución" mostraba el día -1). La fijamos
  // como fecha LOCAL para que el día mostrado coincida con el ingresado.
  const m = RE_SOLO_DIA.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(value);
}

export function formatRelativo(value: Date | string, baseline?: Date): string {
  const fecha = toDate(value);
  return formatDistanceToNow(fecha, {
    addSuffix: true,
    locale: es,
    ...(baseline ? { now: baseline } : {}),
  } as Parameters<typeof formatDistanceToNow>[1]);
}

export function formatAbsoluto(value: Date | string): string {
  const fecha = toDate(value);
  return fecha.toLocaleDateString("es-PE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatAbsolutoConHora(value: Date | string): string {
  const fecha = toDate(value);
  return `${fecha.toLocaleDateString("es-PE", { day: "2-digit", month: "2-digit", year: "numeric" })} ${fecha.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })}`;
}

/**
 * Estado de urgencia derivado de los días restantes.
 * Se usa para colorear `PlazoRestante`.
 */
export type UrgenciaPlazo = "ok" | "proximo" | "critico" | "vencido";

export function calcularUrgencia(diasRestantes: number): UrgenciaPlazo {
  if (diasRestantes < 0) return "vencido";
  if (diasRestantes <= 7) return "critico";
  if (diasRestantes <= 30) return "proximo";
  return "ok";
}

export function diasRestantesHasta(fechaLimite: Date | string, ahora = new Date()): number {
  // Días de CALENDARIO (ignora la hora del día): coincide con el cálculo del
  // backend (`plazo.diasRestantes`) y es lo intuitivo para un vencimiento
  // ("hoy 30, vence el 6 → faltan 7"). Antes usábamos `differenceInDays`, que
  // trunca por la hora parcial y daba un día menos que el panel (6 vs 7).
  return differenceInCalendarDays(toDate(fechaLimite), ahora);
}

export function formatPlazoLegible(
  fechaLimite: Date | string,
  ahora = new Date(),
): { dias: number; urgencia: UrgenciaPlazo; texto: string } {
  const dias = diasRestantesHasta(fechaLimite, ahora);
  const urgencia = calcularUrgencia(dias);
  let texto: string;
  if (dias < 0) {
    texto = `Vencido hace ${formatDistanceToNowStrict(toDate(fechaLimite), { locale: es })}`;
  } else if (dias === 0) {
    texto = "Vence hoy";
  } else if (dias === 1) {
    texto = "Vence mañana";
  } else {
    texto = `Vence en ${dias} días`;
  }
  return { dias, urgencia, texto };
}
